/**
 * Role-based UI gating helpers.
 *
 * Frontend does NOT enforce security — the backend is the source of truth (RBAC via
 * `require_role(...)`). These helpers exist to keep the UI consistent with the
 * user's effective permissions, hiding controls a role cannot use anyway.
 */
import { useAuth } from "../context/AuthContext";

export type EffectiveRole =
  | "super_admin"
  | "owner_admin"
  | "admin_support"
  | "manager_trainer"
  | "representative"
  | "viewer"
  | null;

const RANK: Record<string, number> = {
  super_admin: 100,
  owner_admin: 80,
  admin_support: 70,
  manager_trainer: 60,
  representative: 40,
  viewer: 20,
};

export function useEffectiveRole(): EffectiveRole {
  const { user, supportSession } = useAuth();
  if (!user) return null;
  // Platform super admins acting under a support-access session are treated as owner_admin for CRM UI
  if ((user as any).scope === "platform") {
    if ((user as any).platform_role === "super_admin") {
      return supportSession ? "owner_admin" : "super_admin";
    }
    return null;
  }
  // Tenant users: role is derived from the local user object if present; otherwise defaults to viewer for safety.
  const r = (user as any).tenant_role || (user as any).role || "representative";
  return (r as EffectiveRole);
}

export function canAtLeast(role: EffectiveRole, min: keyof typeof RANK): boolean {
  if (!role) return false;
  return (RANK[role] || 0) >= (RANK[min] || 0);
}

export function useCanWrite(): boolean {
  const r = useEffectiveRole();
  return canAtLeast(r, "representative");
}

export function useIsViewer(): boolean {
  return useEffectiveRole() === "viewer";
}

export function useIsOwnerAdmin(): boolean {
  return canAtLeast(useEffectiveRole(), "owner_admin");
}
